import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Upload,
  SendHorizonal,
  BarChart3,
  ShieldCheck,
  Building2,
  Accessibility,
  FileCheck2,
  Sparkles,
  MapPin,
  Ship,
} from 'lucide-react'
import { useSeo } from '@/lib/useSeo'

export function Home() {
  useSeo({
    title: 'VICE — Virgin Islands Commercial Environment',
    description:
      'VICE uses AI workflow automation to capture and validate line level import and export data from existing digital documents and transmit it to ACE.',
  })

  return (
    <>
      {/* ───────── Hero ───────── */}
      <section className="relative overflow-hidden bg-primary text-white">
        <div
          className="absolute inset-0 opacity-30 bg-center bg-cover"
          style={{ backgroundImage: "url('/images/islands-aerial.jpg')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(10,34,64,0.95) 0%, rgba(10,34,64,0.75) 55%, rgba(46,196,199,0.25) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold uppercase tracking-wider">
              <Sparkles size={14} aria-hidden="true" className="text-accent-sunset" />
              Built for the U.S. Virgin Islands
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Bring USVI entry summaries{' '}
              <span className="bg-vice-sunset bg-clip-text text-transparent">into ACE.</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-200 leading-relaxed max-w-xl">
              VICE uses AI workflow automation to capture and validate line level import and export
              data from existing digital documents and transmit it to ACE, closing the automation
              gap between the U.S. Virgin Islands and every other CBP port.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/signin"
                className="inline-flex items-center gap-2 px-6 py-3 rounded bg-accent-sunset text-primary font-bold hover:bg-accent-sunset/90 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 transition"
              >
                Sign in
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 px-6 py-3 rounded border border-white/30 text-white font-semibold hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
              >
                See the features
              </Link>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div
              className="aspect-[4/3] w-full rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10"
              style={{
                backgroundImage: "url('/images/port-sunset.jpg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              role="img"
              aria-label="USVI port at sunset"
            />
            <div className="absolute -bottom-6 -left-6 bg-white text-primary rounded-lg shadow-xl p-5 max-w-xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-accent-coral uppercase tracking-wider">
                <MapPin size={14} aria-hidden="true" />
                FY2024 baseline
              </div>
              <p className="mt-2 text-3xl font-bold">~28,000 entries</p>
              <p className="mt-1 text-sm text-neutral-500">
                processed manually across USVI ports today with no line level data in ACE.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Trust bar ───────── */}
      <section aria-label="Trust signals" className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Accessibility, label: 'WCAG 2.1 AA / Section 508' },
            { Icon: ShieldCheck, label: 'Row-Level Security + CSP' },
            { Icon: Building2, label: 'Built for the U.S. Virgin Islands' },
            { Icon: FileCheck2, label: 'Protoware License v1.0' },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="p-2 rounded bg-white border border-neutral-200">
                <Icon size={20} className="text-secondary" aria-hidden="true" />
              </div>
              <span className="text-sm font-semibold text-neutral-900">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── What VICE does ───────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            What VICE does
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
            One clean path from document to ACE.
          </h2>
          <p className="mt-4 text-lg text-neutral-700 leading-relaxed">
            The USVI Agent uploads an entry document. VICE handles the rest, from document
            digitization to review and transmission, so the officer can focus on judgment, not
            data entry.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              Icon: Upload,
              title: 'AI Workflow Automation',
              body: 'Review entries in one place with built in support. VICE validates the data, flags issues, and answers questions as agents work.',
              accent: 'bg-accent-coral',
            },
            {
              Icon: SendHorizonal,
              title: 'Transmit to ACE',
              body: 'Review the entry data, confirm it is correct, and transmit it to ACE.',
              accent: 'bg-accent-sunset',
            },
            {
              Icon: BarChart3,
              title: 'USVI specific reports',
              body: 'Reporting is structured to show how shipment volume, classifications, and duties contribute to the Virgin Islands Deposit Fund.',
              accent: 'bg-accent-turquoise',
            },
          ].map(({ Icon, title, body, accent }) => (
            <article
              key={title}
              className="group relative bg-white border border-neutral-200 rounded-lg p-6 shadow-sm hover:shadow-md transition overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} aria-hidden="true" />
              <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary">
                <Icon size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-primary">{title}</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{body}</p>
              <Link
                to="/features"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary group-hover:text-primary focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 rounded"
              >
                Learn more <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* ───────── How it works ───────── */}
      <section className="bg-secondary-lightest py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
              How it works
            </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
            Four steps, fully auditable.
          </h2>
          <p className="mt-4 text-lg text-neutral-700 leading-relaxed">
            Every step is logged. Every AWA decision is explainable. Every submission is reviewed
            and protected before it is transmitted to ACE.
          </p>
        </div>

          <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 relative">
            {[
              {
                n: '01',
                title: 'Upload',
                body: 'USVI Agent uploads the source entry document.',
              },
              {
                n: '02',
                title: 'AWA',
                body: 'Text extraction, field organization, validation, confidence scoring.',
              },
              {
                n: '03',
                title: 'Review',
                body: 'Agent confirms or corrects flagged items with support from the AI chat assistant.',
              },
              {
                n: '04',
                title: 'Transmit',
                body: 'Every submission is delivered to ACE and recorded for audit and tracking.',
              },
            ].map(({ n, title, body }) => (
              <li
                key={n}
                className="relative bg-white rounded-lg p-6 border border-neutral-200 shadow-sm"
              >
                <div className="text-[11px] font-mono font-bold text-accent-coral tracking-wider">
                  STEP {n}
                </div>
                <h3 className="mt-2 text-lg font-bold text-primary">{title}</h3>
                <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────── USVI context — paired image + copy ───────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid gap-12 lg:grid-cols-2 items-center">
        <div className="order-2 lg:order-1">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            Why the islands need this
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
            The USVI is a separate customs territory.
          </h2>
          <div className="mt-6 space-y-4 text-neutral-700 leading-relaxed">
            <p>
              Under <a href="https://www.ecfr.gov/current/title-19/chapter-I/part-7/section-7.2" target="_blank" rel="noreferrer" className="text-secondary underline decoration-accent-turquoise underline-offset-4">19 CFR § 7.2</a>, the U.S. Virgin Islands sit
              <strong> outside the U.S. customs territory</strong>. The territory applies its own
              duty rates under local law, while <strong>CBP enforces</strong> applicable federal
              laws at the border. U.S. trade agreement preferences and Section 321 de minimis
              generally do not apply to imports into the USVI.
            </p>
            <p>
              VICE aligns USVI entry summary data with federal data standards, improving visibility
              across systems. With line level data available, risk targeting, revenue protection,
              and reporting become more effective.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            <div className="p-4 rounded border border-neutral-200 bg-neutral-50">
              <div className="text-3xl font-bold text-accent-coral">0</div>
              <p className="text-xs text-neutral-600 mt-1 leading-snug">
                Line level entries in ACE today
              </p>
            </div>
            <div className="p-4 rounded border border-neutral-200 bg-neutral-50">
              <div className="text-3xl font-bold text-accent-turquoise">100%</div>
              <p className="text-xs text-neutral-600 mt-1 leading-snug">
                Entry summaries manually keyed
              </p>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <div className="grid grid-cols-2 gap-4">
            <div
              className="aspect-square rounded-lg bg-cover bg-center shadow-md"
              style={{ backgroundImage: "url('/images/cargo-containers.jpg')" }}
              role="img"
              aria-label="Stacked shipping containers"
            />
            <div
              className="aspect-square rounded-lg bg-cover bg-center shadow-md mt-8"
              style={{ backgroundImage: "url('/images/cargo-ship.jpg')" }}
              role="img"
              aria-label="Cargo ship approaching port"
            />
            <div
              className="aspect-square rounded-lg bg-cover bg-center shadow-md -mt-4"
              style={{ backgroundImage: "url('/images/container-ship.jpg')" }}
              role="img"
              aria-label="Container ship at sea"
            />
            <div
              className="aspect-square rounded-lg bg-cover bg-center shadow-md mt-4"
              style={{ backgroundImage: "url('/images/hero-islands.jpg')" }}
              role="img"
              aria-label="USVI islands from above"
            />
          </div>
        </div>
      </section>

      {/* ───────── Persona ───────── */}
      <section className="bg-neutral-50 py-20 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
              Who uses VICE
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
              Built for USVI agents.
            </h2>
            <p className="mt-4 text-lg text-neutral-700">
              Trained agents process entry summaries from intake through transmission to ACE.
              Accounts are provisioned by invitation.
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <article className="bg-white rounded-lg p-6 border-2 border-accent-sunset shadow-sm">
              <div className="inline-flex p-3 rounded bg-accent-sunset/10 text-accent-sunset">
                <Ship size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-3 text-lg font-bold text-primary">USVI Agents</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                Run the full intake → review → transmit → archive cycle. Every step is logged and
                auditable.
              </p>
            </article>
            <article className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary">
                <Sparkles size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-3 text-lg font-bold text-primary">AWA assistance</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                AI-assisted extraction, validation, and recommendations handle repetitive work so
                agents can focus on judgment.
              </p>
            </article>
            <article className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary">
                <FileCheck2 size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-3 text-lg font-bold text-primary">Auditable by design</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                Every AWA decision is explainable and every transmission archived for compliance
                and reporting.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ───────── Final CTA ───────── */}
      <section className="relative bg-primary text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-center bg-cover"
          style={{ backgroundImage: "url('/images/port-sunset.jpg')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(10,34,64,0.95) 0%, rgba(10,34,64,0.8) 60%, rgba(255,138,61,0.35) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 text-center">
          <div className="mx-auto w-16 h-1 bg-vice-sunset rounded mb-8" aria-hidden="true" />
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
            Built for the islands. Trusted at the port.
          </h2>
          <p className="mt-6 text-lg text-neutral-200 max-w-2xl mx-auto">
            Ready to improve how entry data is reviewed and transmitted to ACE?
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signin"
              className="inline-flex items-center gap-2 px-6 py-3 rounded bg-accent-sunset text-primary font-bold hover:bg-accent-sunset/90 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            >
              Sign in
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded border border-white/30 font-semibold hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
